import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSocket } from '@/hooks/useSocket';
import { useWakeLock } from '@/hooks/useWakeLock';
import { trpc } from '@/trpc/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import LiveMap from '@/components/LiveMap';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';

interface LocationEvent {
  jobId: string;
  transporterId: string;
  latitude: number;
  longitude: number;
  timestamp: string;
}

const ClientJobTrackingPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  // Store locations by transporterId
  const [locations, setLocations] = useState<Record<string, LocationEvent>>({});
  // Track number of updates received per transporter for this session
  const [updateCounts, setUpdateCounts] = useState<Record<string, number>>(() => {
    // Try to load from local storage
    try {
      if (!jobId) return {};
      const saved = localStorage.getItem(`client_tracking_counts_${jobId}`);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Keep screen awake while tracking
  useWakeLock();

  const [darkMode, setDarkMode] = useState(() => {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  // Handle location updates from socket
  const handleLocationUpdate = useCallback(
    (data: LocationEvent) => {
      console.log('📍 Frontent received location update:', data);
      if (data.jobId === jobId) {
        console.log('✅ Update matches current job:', jobId);
        setLocations(prev => ({
          ...prev,
          [data.transporterId]: data
        }));
        setUpdateCounts(prev => {
          const newCounts = {
            ...prev,
            [data.transporterId]: (prev[data.transporterId] || 0) + 1
          };
          // Persist to local storage
          localStorage.setItem(`client_tracking_counts_${jobId}`, JSON.stringify(newCounts));
          return newCounts;
        });
      }
    },
    [jobId],
  );

  // Initialize socket with location handler
  const {
    socket,
    isConnected,
    joinJobRoom,
    leaveJobRoom,
    lastLocationUpdate,
  } = useSocket({
    onLocationUpdate: handleLocationUpdate,
    enableDebugLogs: true,
    silentMode: true,
  });

  // Fetch job details
  const {
    data: job,
    isLoading,
    refetch: refetchJob,
  } = trpc.job.getById.useQuery(
    { id: jobId! },
    { enabled: !!jobId, refetchInterval: 30000 },
  );

  // Authorize tracking and get initial locations
  const clientAuthQuery = trpc.location.clientAuthorizeJobTracking.useQuery(
    { jobId: jobId! },
    {
      enabled: !!jobId && isConnected,
      retry: 2,
      onSuccess: (data) => {
        console.log('🔓 Auth Query Success:', data);
        if (data.activeLocations && Array.isArray(data.activeLocations)) {
          const locMap: Record<string, LocationEvent> = {};
          data.activeLocations.forEach((loc: LocationEvent) => {
            locMap[loc.transporterId] = loc;
          });
          setLocations(locMap);
        }
      },
    },
  );

  // Auto re-authorize when socket reconnects
  useEffect(() => {
    if (jobId && isConnected) {
      clientAuthQuery.refetch();
    }
  }, [jobId, isConnected]);

  // Join/leave job room on mount/unmount
  useEffect(() => {
    if (jobId && isConnected && socket) {
      console.log('Joining job room:', jobId);
      joinJobRoom(jobId);
      return () => {
        console.log('Leaving job room:', jobId);
        leaveJobRoom(jobId);
      };
    } else if (jobId && !isConnected) {
      console.log('Waiting for socket connection to join room:', jobId);
    }
  }, [jobId, isConnected, socket, joinJobRoom, leaveJobRoom]);

  // Sync lastLocationUpdate from socket hook
  useEffect(() => {
    if (lastLocationUpdate && lastLocationUpdate.jobId === jobId) {
      setLocations(prev => ({
        ...prev,
        [lastLocationUpdate.transporterId]: lastLocationUpdate
      }));
    }
  }, [lastLocationUpdate, jobId]);

  // Ultra-fast polling for redundancy (Hybrid Mode)
  // We poll every 3 seconds regardless of socket state to ensure data consistency
  const latestLocationQuery = trpc.location.getLatestJobLocation.useQuery(
    { jobId: jobId! },
    {
      enabled: !!jobId, // Always enabled if we have a Job ID
      refetchInterval: 3000 // 3 seconds heartbeat 
    },
  );

  useEffect(() => {
    if (latestLocationQuery.data?.locations) {
      const locMap: Record<string, LocationEvent> = {};
      latestLocationQuery.data.locations.forEach((loc: LocationEvent) => {
        locMap[loc.transporterId] = loc;
      });
      setLocations(locMap);
    }
  }, [latestLocationQuery.data]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Prepare map data
  const pickup =
    job?.pickupLat && job?.pickupLng
      ? {
        lat: Number(job.pickupLat),
        lng: Number(job.pickupLng),
        label: job.pickupDistrict,
      }
      : null;

  const delivery =
    job?.deliveryLat && job?.deliveryLng
      ? {
        lat: Number(job.deliveryLat),
        lng: Number(job.deliveryLng),
        label: job.deliveryDistrict,
      }
      : null;

  // Prepare Vehicles for Map
  const vehicles = useMemo(() => {
    // Merge with driver info if available from clientAuthQuery
    const assignedDrivers = clientAuthQuery.data?.assignedDrivers || [];

    return Object.values(locations).map(loc => {
      const driver = assignedDrivers.find((d: any) => d.id === loc.transporterId);
      return {
        id: loc.transporterId,
        lat: loc.latitude,
        lng: loc.longitude,
        label: driver?.name || 'Driver'
      };
    });
  }, [locations, clientAuthQuery.data]);

  const activeDriverCount = vehicles.length;
  const isMultidriver = (clientAuthQuery.data?.assignedDrivers?.length || 0) > 1;

  // Most recent timestamp
  const lastUpdateTimestamp = useMemo(() => {
    const timestamps = Object.values(locations).map(l => new Date(l.timestamp).getTime());
    if (timestamps.length === 0) return null;
    return Math.max(...timestamps);
  }, [locations]);

  const locationAge = lastUpdateTimestamp
    ? Math.floor((Date.now() - lastUpdateTimestamp) / 1000)
    : null;

  const isLocationStale = locationAge ? locationAge > 120 : false;

  return (
    <div className="container mx-auto py-6 px-2 sm:px-4 animate-fade-in">
      <Card className="max-w-6xl mx-auto shadow-xl border-primary/20">
        <CardHeader className="border-b p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                Live Tracking
                <Badge variant="outline" className="font-mono text-xs">#{jobId?.slice(0, 8)}</Badge>
                {/* Connection Quality Indicator */}
                <Badge variant={isConnected ? "default" : "secondary"} className={`text-xs ml-2 ${isConnected ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' : ''}`}>
                  {isConnected ? '⚡ Real-time' : '📡 Polling'}
                </Badge>
              </CardTitle>
              {job && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-muted-foreground">
                    {job.pickupDistrict} &rarr; {job.deliveryDistrict}
                  </p>
                  {isMultidriver && (
                    <Badge variant="secondary" className="text-xs h-5">
                      <Users className="w-3 h-3 mr-1" />
                      Fleet Delivery
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  latestLocationQuery.refetch();
                  clientAuthQuery.refetch();
                  toast.success('Refreshing location data...');
                }}
                className="hidden sm:flex"
              >
                🔄 Refresh
              </Button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Toggle theme"
              >
                {darkMode ? '🌙' : '☀️'}
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Connection Status - Only show if both are failing, otherwise showing Polling badge is enough */}
          {!isConnected && !latestLocationQuery.data && (
            <div className="p-4 bg-destructive/10 border-b border-destructive/20">
              <AlertDescription className="flex items-center gap-2 text-sm text-destructive">
                <span className="animate-pulse">!</span>
                Real-time connection lost. Switching to backup satellite link...
              </AlertDescription>
            </div>
          )}

          {/* Stale Location Warning */}
          {isLocationStale && activeDriverCount > 0 && (
            <div className="p-4 bg-yellow-500/10 border-b border-yellow-500/20">
              <AlertDescription className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <span>⚠️</span>
                Vehicle signal is weak (Last update: {locationAge}s ago).
                <Button
                  variant="link"
                  className="h-auto p-0 ml-2 text-yellow-600 underline"
                  onClick={() => latestLocationQuery.refetch()}
                >
                  Try Force Refresh
                </Button>
              </AlertDescription>
            </div>
          )}

          {/* No Active Drivers */}
          {!isLoading && activeDriverCount === 0 && (
            <div className="p-4 bg-blue-500/10 border-b border-blue-500/20">
              <AlertDescription className="text-sm text-blue-600 dark:text-blue-400">
                ℹ️ Waiting for drivers to come online...
              </AlertDescription>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="h-[400px] sm:h-[500px] flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin text-4xl mb-4 text-primary">⏳</div>
                <p className="text-muted-foreground">Loading mission data...</p>
              </div>
            </div>
          )}

          {/* Map */}
          {!isLoading && (
            <div className="h-[400px] sm:h-[600px] relative">
              <LiveMap
                vehicles={vehicles}
                pickup={pickup}
                delivery={delivery}
                height="100%"
                zoom={9}
                darkMode={darkMode}
                showRealRoute={true}
                animateRoute={true}
              />

              {/* Floating Refresh (Mobile) */}
              <div className="absolute top-4 right-4 sm:hidden z-10">
                <Button
                  size="icon"
                  className="rounded-full shadow-lg bg-primary text-primary-foreground"
                  onClick={() => {
                    latestLocationQuery.refetch();
                    toast.success('Scanning for updates...');
                  }}
                >
                  🔄
                </Button>
              </div>
            </div>
          )}

          {/* Status Footer */}
          <div className="border-t bg-muted/20 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Connection Status */}
              <div className="flex items-center gap-2 group relative">
                <div
                  className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500 animate-pulse'
                    }`}
                />
                <span className="text-sm font-medium">
                  {isConnected ? 'Live Feed Active' : 'Satellite Polling Active (3s)'}
                </span>

                {/* Debug Tooltip */}
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-black/80 text-white text-[10px] p-2 rounded whitespace-nowrap z-50">
                  Socket ID: {window.socket?.id || 'N/A'}<br />
                  Mode: {isConnected ? 'WebSocket' : 'Polling'}<br />
                  Room: {jobId ? `job:${jobId}` : 'None'}
                </div>
              </div>

              {/* Active Drivers Count */}
              <div className="text-sm text-center">
                <span className="font-medium">Active Units: </span>
                <span className="font-mono">{activeDriverCount}</span>
                {clientAuthQuery.data?.assignedDrivers && (
                  <span className="text-muted-foreground text-xs ml-1">
                    / {clientAuthQuery.data.assignedDrivers.length} assigned
                  </span>
                )}
              </div>

              {/* Last Update */}
              <div className="text-sm text-right">
                {lastUpdateTimestamp && (
                  <div>
                    <span className="font-medium">Last Contact: </span>{' '}
                    <span className="text-muted-foreground">
                      {locationAge && locationAge < 5 ? 'Just now' : new Date(lastUpdateTimestamp).toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Drivers List (if multiple) */}
      {vehicles.length > 0 && (
        <div className="max-w-6xl mx-auto mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map(v => (
            <Card key={v.id} className="bg-card/50">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{v.label}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {v.lat.toFixed(4)}, {v.lng.toFixed(4)}
                  </p>
                  <p className="text-[10px] text-blue-500 mt-1 font-medium bg-blue-500/10 inline-block px-2 py-0.5 rounded-full">
                    📡 {updateCounts[v.id] || 0} updates received
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Additional Info Card (retained from original) */}
      {job && (
        <Card className="max-w-6xl mx-auto mt-6 border-primary/10">
          <CardHeader>
            <CardTitle className="text-lg">Delivery Manifest</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pickup Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 font-semibold text-green-600 dark:text-green-400">
                  Pickup Location
                </div>
                <div className="pl-6 space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">District: </span>{' '}
                    <span className="font-medium">{job.pickupDistrict}</span>
                  </div>
                  {job.pickupAddress && (
                    <div>
                      <span className="text-muted-foreground">Address: </span>{' '}
                      <span>{job.pickupAddress}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 font-semibold text-red-600 dark:text-red-400">
                  Delivery Location
                </div>
                <div className="pl-6 space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">District: </span>{' '}
                    <span className="font-medium">{job.deliveryDistrict}</span>
                  </div>
                  {job.deliveryAddress && (
                    <div>
                      <span className="text-muted-foreground">Address: </span>{' '}
                      <span>{job.deliveryAddress}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientJobTrackingPage;
